import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Archive, RotateCcw, Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

const StatusBadge = ({ status }) => {
  const styles = {
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    confirmed: "bg-green-100 text-green-800 border-green-200",
    cancelled: "bg-red-100 text-red-700 border-red-200",
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${styles[status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
      {status}
    </span>
  );
};

export default function AdminBookingArchives() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [unarchiving, setUnarchiving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchArchived = () => {
    setLoading(true);
    api.getArchivedBookings()
      .then((r) => setBookings(r.data.bookings))
      .catch(() => toast.error("Failed to load archived bookings"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchArchived(); }, []);

  const filtered = bookings.filter((b) => {
    if (statusFilter !== "all" && b.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (b.requester_name || "").toLowerCase().includes(q) ||
             (b.requester_email || "").toLowerCase().includes(q);
    }
    return true;
  });

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((b) => b.id)));
    }
  };

  const handleUnarchive = async () => {
    if (selected.size === 0) return;
    setUnarchiving(true);
    try {
      await api.unarchiveBookings([...selected]);
      toast.success(`${selected.size} booking(s) restored`);
      setSelected(new Set());
      fetchArchived();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to unarchive bookings");
    } finally {
      setUnarchiving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Booking Archives</h1>
          <p className="text-slate-500 mt-1">{bookings.length} archived booking{bookings.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex gap-2">
          {selected.size > 0 && (
            <Button
              variant="outline"
              onClick={handleUnarchive}
              disabled={unarchiving}
              className="gap-2 border-green-200 text-green-700 hover:bg-green-50"
              data-testid="unarchive-bookings-btn"
            >
              {unarchiving ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              Restore ({selected.size})
            </Button>
          )}
        </div>
      </div>

      {/* Search and filter */}
      <div className="flex gap-3 mb-5">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10"
            data-testid="archive-search"
          />
        </div>
        <div className="flex gap-2">
          {["all", "pending", "confirmed", "cancelled"].map((f) => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`px-3 py-2 rounded-full text-xs font-medium transition-all ${
                statusFilter === f ? "bg-slate-700 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-green-100 shadow-sm overflow-hidden" data-testid="archived-bookings-table">
        {loading ? (
          <div className="p-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <Archive className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No archived bookings{search ? " matching your search" : ""}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <input type="checkbox" className="w-4 h-4 accent-green-700"
                      checked={filtered.length > 0 && selected.size === filtered.length}
                      onChange={toggleSelectAll}
                      data-testid="select-all-archived" />
                  </th>
                  <th className="text-left px-5 py-3 font-semibold">Requester</th>
                  <th className="text-left px-5 py-3 font-semibold">Date & Time</th>
                  <th className="text-left px-5 py-3 font-semibold">Status</th>
                  <th className="text-left px-5 py-3 font-semibold">Volunteer</th>
                  <th className="text-left px-5 py-3 font-semibold">Archived</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b, i) => (
                  <tr key={b.id} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"} data-testid={`archived-row-${b.id}`}>
                    <td className="px-3 py-3">
                      <input type="checkbox" className="w-4 h-4 accent-green-700"
                        checked={selected.has(b.id)}
                        onChange={() => toggleSelect(b.id)}
                        data-testid={`select-archived-${b.id}`} />
                    </td>
                    <td className="px-5 py-3">
                      <p className="font-medium text-slate-800">{b.requester_name}</p>
                      <p className="text-slate-400 text-xs">{b.requester_email}</p>
                    </td>
                    <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{b.booking_dt_formatted}</td>
                    <td className="px-5 py-3"><StatusBadge status={b.status} /></td>
                    <td className="px-5 py-3 text-slate-500">{b.claimed_by_name || "—"}</td>
                    <td className="px-5 py-3 text-slate-400 text-xs whitespace-nowrap">
                      {b.archived_at ? new Date(b.archived_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
